import { useState } from "react";
import { Section } from "@/components/Section";
import { Container } from "@/components/Container";
import { useQuery } from "@tanstack/react-query";
import { getRecognitions } from "@/lib/recognitions.functions";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, ZoomIn } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export function RecognitionsSection() {
  const { data: recognitions, isLoading } = useQuery({
    queryKey: ["recognitions"],
    queryFn: () => getRecognitions(),
  });

  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  if (isLoading || !recognitions || recognitions.length === 0) return null;

  return (
    <Section className="bg-secondary/20">
      <Container>
        <div className="text-center mb-12">
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Recognition & Awards
          </span>
          <h2 className="mt-4 font-display text-3xl font-medium md:text-4xl">
            Recognised for our commitment to healthy, quality meals.
          </h2>
        </div>

        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {recognitions.map((recognition) => (
            <motion.div
              key={recognition.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="group relative flex flex-col items-center p-6 bg-background rounded-2xl border border-border transition-all hover:shadow-lg"
            >
              <div 
                className="relative aspect-[4/3] w-full mb-6 overflow-hidden rounded-lg cursor-pointer bg-muted"
                onClick={() => setSelectedImage(recognition.image_url)}
              >
                <img
                  src={recognition.image_url}
                  alt={recognition.title}
                  className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/5 flex items-center justify-center">
                  <ZoomIn className="text-white opacity-0 transition-opacity group-hover:opacity-100 h-8 w-8" />
                </div>
              </div>

              <div className="text-center">
                <h3 className="font-display text-xl font-medium">{recognition.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {recognition.organization} • {recognition.year}
                </p>
                {recognition.description && (
                  <p className="text-sm text-muted-foreground mt-3 italic">
                    "{recognition.description}"
                  </p>
                )}
                <Button
                  variant="link"
                  className="mt-4 text-primary p-0 h-auto"
                  onClick={() => setSelectedImage(recognition.image_url)}
                >
                  View Recognition
                </Button>
              </div>
            </motion.div>
          ))}
        </div>

        <Dialog open={!!selectedImage} onOpenChange={(open) => !open && setSelectedImage(null)}>
          <DialogContent className="max-w-4xl p-0 overflow-hidden bg-transparent border-none shadow-none">
            <DialogTitle className="sr-only">Certificate View</DialogTitle>
            <div className="relative flex items-center justify-center p-4 min-h-[50vh]">
              <DialogClose className="absolute right-4 top-4 z-50 rounded-full bg-black/50 p-2 text-white hover:bg-black/70">
                <X className="h-6 w-6" />
              </DialogClose>
              <AnimatePresence mode="wait">
                {selectedImage && (
                  <motion.img
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    src={selectedImage}
                    alt="Full Recognition Certificate"
                    className="max-w-full max-h-[85vh] object-contain rounded-lg"
                  />
                )}
              </AnimatePresence>
            </div>
          </DialogContent>
        </Dialog>
      </Container>
    </Section>
  );
}
